<template>
  <div class="mpa-placement" :class="{ dragging: !!draggedGroup }">
    <!-- What the product settings call this, because it is the same thing: the placement written
         to the generic template product. `MULTI_CREATION.PANEL.HEADING` is the page title. -->
    <h2>{{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_POSITION.HEADING') }}</h2>
    <p
      class="subheading"
      v-html="$t('DESIGNS.MULTI_CREATION.PANEL.SUBHEADING')"
    ></p>
    <SmartFaqLink :link="smartFaqLinks.PRINT_AREAS_PRICING" />

    <ul class="print-areas">
      <li v-for="(printArea, group) in printAreas" :key="group">
        <strong>{{ $t(printArea.name) }}</strong>
        <div class="slot">
          <div
            v-if="selection.placement.value[group]"
            class="design"
            :draggable="true"
            @dragstart="onDragStart($event, group)"
            @dragend="onDragEnd"
          >
            <Icon icon="drag" />
            <SprdImage
              class="thumbnail"
              :src="`${selection.placement.value[group].href},height=50,width=50`"
              alt=""
            />
            <p class="name text-truncate">
              {{
                selection.placement.value[group].name ||
                `${$t('DESIGNS.ID')} ${selection.placement.value[group].id}`
              }}
            </p>
            <div class="actions">
              <button
                class="btn icon-only-btn link-red remove"
                @click="selection.removeFrom(group)"
              >
                <Icon icon="bin" :draggable="false" />
              </button>
              <button
                v-tooltip="{
                  content: $t('DESIGNS.MULTI_CREATION.MAIN_DESIGN_EXPLANATION'),
                  delay: 400,
                }"
                class="btn icon-only-btn link-main main-design"
                :aria-label="$t('DESIGNS.MULTI_CREATION.MAIN_DESIGN')"
                @click="
                  selection.setMainDesign(selection.placement.value[group].id)
                "
              >
                <Icon
                  :icon="
                    selection.isMainDesign(selection.placement.value[group].id)
                      ? 'star-filled'
                      : 'star'
                  "
                  :draggable="false"
                />
              </button>
            </div>
          </div>
          <div v-else class="placeholder" />

          <div
            v-if="draggedGroup && draggedGroup !== group"
            class="placeholder drop-target"
            :class="{ 'drop-target--over': overGroup === group }"
            @dragover.prevent="overGroup = group"
            @dragleave.prevent="overGroup = null"
            @drop.prevent.stop="onDrop(group)"
          >
            {{
              $t(
                selection.placement.value[group]
                  ? 'DESIGNS.PRINTAREA.FEEDBACK.EXCHANGE'
                  : 'DESIGNS.PRINTAREA.FEEDBACK.CHANGE',
              )
            }}
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import SprdImage from 'commons/image/SprdImage.vue';
import SmartFaqLink from 'commons/helpLink/SmartFaqLink.vue';
import smartFaqLinks from 'commons/smartFaq/smartFaqLinks';
import { printAreas } from 'commons/composition/compositionHelper';
import type { useMultiPrintAreaSelection } from 'commons/composition/multiPrintAreaSelection';

/**
 * One slot per print area. The exact hotspot is deliberately not asked for here — see
 * `docs/publishing/multi-print-area-creation.md` §7.
 */
const props = defineProps<{
  selection: ReturnType<typeof useMultiPrintAreaSelection>;
}>();

const selection = props.selection;

const draggedGroup = ref<string | null>(null);
const overGroup = ref<string | null>(null);

const onDragStart = (event: DragEvent, group: string) => {
  // Firefox starts no drag without a payload.
  event.dataTransfer?.setData('text', '');
  draggedGroup.value = group;
};

const onDragEnd = () => {
  draggedGroup.value = null;
  overGroup.value = null;
};

const onDrop = (group: string) => {
  if (draggedGroup.value) selection.move(draggedGroup.value, group);
  onDragEnd();
};
</script>

<style scoped lang="scss">
@import 'src/scss/constants';
@import 'src/scss/styleguide/colors';

// Stays put while the design list scrolls under it, so the background is required, not decoration.
// Layout constraints: `docs/publishing/multi-print-area-creation.md` §3.
.mpa-placement {
  position: sticky;
  top: 24px;
  // Above the design tiles, which use up to z-index 5 internally (`Tile.vue`).
  z-index: 10;
  background-color: $grey0;
  border: 1px solid $grey10;
  border-radius: $border-radius-medium;
  padding: 16px;
}

h2 {
  margin: 0 0 8px 0;
}

.subheading {
  margin: 0;
  color: $grey60;
}

// Same spacing the flexi journey gave this link (`PrintAreaSelector`), so it does not stick to the
// paragraph above it.
.help-link {
  margin-top: 16px;
}

.print-areas {
  list-style: none;
  display: grid;
  // Fixed column count: `auto-fit` and `flex-wrap` leave an odd last row.
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  padding: 0;
  margin: 16px 0 0 0;

  @media (max-width: 1099px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  li {
    // Lets a long design name truncate instead of widening its column.
    min-width: 0;
  }

  strong {
    display: block;
    margin-bottom: 4px;
  }
}

.slot {
  position: relative;
  height: 64px;
}

.design {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid $grey20;
  border-radius: $border-radius-medium;
  background-color: $grey0;
  cursor: move;

  > .icon {
    flex-shrink: 0;
  }

  .thumbnail {
    flex-shrink: 0;
    pointer-events: none;
  }

  .name {
    flex-grow: 1;
    // Lets `text-truncate` actually truncate rather than push the card wider.
    min-width: 0;
    margin: 0;
  }

  .actions {
    // Top right and bottom right corner, as the flexi journey had it.
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-self: stretch;
    flex-shrink: 0;

    .icon {
      width: 18px;
      height: 18px;
    }
  }
}

.dragging .design {
  opacity: 0.5;
}

.placeholder {
  height: 100%;
  border: 1px dashed $grey20;
  border-radius: $border-radius-medium;
  // A dashed outline on the page background reads as nothing at all.
  background-color: $grey5;
}

.drop-target {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eef7ff;
  color: $pa-color-main;
  font-weight: bold;

  &--over {
    border-color: $pa-color-main;
  }
}
</style>
